import { useCallback, useEffect, useState } from "react";
import { Card, CardBody, Col, Input, Label, Row, Spinner, Table } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { get, post } from "../../../../utils/helpers/api";
import { useSettings } from "../../../../context/settings";
import { useAuth } from "../../../../context/auth";
import { printProductionOrderStatus, nextStatuses, isOrderEditable } from "../../utils/helpers";
import "../Home/styles.css";

const STATUS_META = {
  confirmed:   { labelKey: 'production.confirmOrder',  color: 'info' },
  in_progress: { labelKey: 'production.startOrder',    color: 'warning' },
  done:        { labelKey: 'production.finishOrder',   color: 'success' },
  cancelled:   { labelKey: 'production.cancelOrder',   color: 'danger' },
};

function ViewProductionOrderPage() {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const { can } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [quantityProduced, setQuantityProduced] = useState('');
  const [pendingStatus, setPendingStatus] = useState(null);

  const getOrder = useCallback(async () => {
    setLoading(true);
    const data = await get('/production/orders/view/' + id);
    if (data?.success) {
      setOrder(data.production_order);
      setQuantityProduced(String(data.production_order.quantity_planned ?? ''));
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    getOrder();
  }, [getOrder]);

  const changeStatus = async (status) => {
    setActionLoading(true);
    const payload = { status };
    if (status === 'done') {
      payload.quantity_produced = Number(quantityProduced || order.quantity_planned);
    }

    const data = await post('/production/orders/update-status/' + id, payload);
    if (data?.success) {
      toast.success(t('production.statusUpdated'));
      setOrder(data.production_order);
    } else if (data?.message) {
      toast.error(data.message);
    }
    setActionLoading(false);
  };

  if (loading || !order) {
    return (
      <SidebarLayout sidebar={sidebar}>
        <div className="page-with-subheader">
          <PageSubHeader
            breadcrumb={[t('sidebar.productionOrders'), t('common.loading')]}
            onBack={() => navigate('/production/orders')}
          />
          <div className="subheader-page-content">
            <Spinner color="primary" className="d-block mx-auto my-4" />
          </div>
        </div>
      </SidebarLayout>
    );
  }

  const available = can('production.orders.edit') ? nextStatuses(order.status) : [];

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('sidebar.productionOrders'), order.order_number]}
          onBack={() => navigate('/production/orders')}
          onReload={getOrder}
        >
          {can('production.orders.edit') && isOrderEditable(order) && (
            <DefaultButton
              color="secondary"
              size="sm"
              outline
              onClick={() => navigate('/production/orders/edit/' + order.id, { state: { productionOrderData: order } })}
            >
              {t('common.edit')}
            </DefaultButton>
          )}
          {available.map((status) => (
            <DefaultButton
              key={status}
              color={STATUS_META[status].color}
              size="sm"
              className="ms-2"
              disabled={actionLoading}
              onClick={() => setPendingStatus(status)}
            >
              {t(STATUS_META[status].labelKey)}
            </DefaultButton>
          ))}
        </PageSubHeader>

        <div className="subheader-page-content scrollable">
          <Row className="g-3 mb-3">
            <Col md="8">
              <Card>
                <CardBody>
                  <Row className="g-3">
                    <Col md="6">
                      <div className="text-muted small">{t('production.product')}</div>
                      <strong>{order.product?.name ?? order.product_name ?? '-'}</strong>
                    </Col>
                    <Col md="3">
                      <div className="text-muted small">{t('common.status')}</div>
                      {printProductionOrderStatus(order.status)}
                    </Col>
                    <Col md="3">
                      <div className="text-muted small">{t('sidebar.billsOfMaterials')}</div>
                      <strong>{order.bill_of_material?.reference ?? '-'}</strong>
                    </Col>

                    <Col md="3">
                      <div className="text-muted small">{t('production.quantityPlanned')}</div>
                      <strong>{order.quantity_planned}</strong>
                    </Col>
                    <Col md="3">
                      <div className="text-muted small">{t('production.quantityProduced')}</div>
                      <strong>{order.quantity_produced}</strong>
                    </Col>
                    <Col md="3">
                      <div className="text-muted small">{t('production.plannedStartDate')}</div>
                      <strong>{order.planned_start_date ? formatDate(order.planned_start_date) : '-'}</strong>
                    </Col>
                    <Col md="3">
                      <div className="text-muted small">{t('production.plannedEndDate')}</div>
                      <strong>{order.planned_end_date ? formatDate(order.planned_end_date) : '-'}</strong>
                    </Col>
                  </Row>

                  {order.notes && (
                    <div className="mt-3">
                      <div className="text-muted small">{t('common.notes')}</div>
                      <div>{order.notes}</div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col md="4">
              <Card className="h-100">
                <CardBody>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">{t('production.totalCost')}</span>
                    <strong>{Number(order.total_cost ?? 0).toFixed(2)}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">{t('production.unitCost')}</span>
                    <strong>{Number(order.unit_cost ?? 0).toFixed(2)}</strong>
                  </div>

                  {order.status === 'in_progress' && (
                    <div>
                      <Label className="small">{t('production.quantityProducedActual')}</Label>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        bsSize="sm"
                        value={quantityProduced}
                        onChange={(e) => setQuantityProduced(e.target.value)}
                      />
                      <div className="text-muted small mt-1">{t('production.quantityProducedHint')}</div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <h6 className="fw-bold mb-2">{t('production.components')}</h6>
          <Table bordered className="bg-white">
            <thead>
              <tr>
                <th>{t('production.component')}</th>
                <th>{t('common.description')}</th>
                <th width="140px">{t('production.quantityRequired')}</th>
                <th width="140px">{t('production.quantityConsumed')}</th>
                <th width="140px">{t('production.unitCost')}</th>
                <th width="140px">{t('common.total')}</th>
              </tr>
            </thead>
            <tbody>
              {(order.lines ?? []).map((line) => (
                <tr key={line.id}>
                  <td>{line.product_name ?? '-'}</td>
                  <td>{line.description ?? '-'}</td>
                  <td>{line.quantity_required}</td>
                  <td>{line.quantity_consumed}</td>
                  <td>{Number(line.unit_cost ?? 0).toFixed(2)}</td>
                  <td>{Number(line.total_cost ?? 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!pendingStatus}
        toggle={() => setPendingStatus(null)}
        title={pendingStatus ? t(STATUS_META[pendingStatus].labelKey) : ''}
        message={pendingStatus ? t('production.confirmStatusChange.' + pendingStatus) : ''}
        confirmationText={pendingStatus ? t(STATUS_META[pendingStatus].labelKey) : ''}
        confirmColor={pendingStatus ? STATUS_META[pendingStatus].color : 'danger'}
        onConfirm={() => changeStatus(pendingStatus)}
      />
    </SidebarLayout>
  );
}

export default ViewProductionOrderPage;
